import {
  limitToFirst,
  onValue,
  query,
  ref,
} from "firebase/database";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { database } from "../firebase/firebase";

interface EntityRecord<T> {
  id: string;
  data: T;
}

interface CatalogContextValue {
  drivers: EntityRecord<any>[];
  vehicles: EntityRecord<any>[];
  suggestions: string[];
  driversMap: Record<string, any>;
  vehiclesMap: Record<string, any>;
  loading: boolean;
}

const CatalogContext = createContext<CatalogContextValue | undefined>(undefined);

export const CatalogProvider = ({ children }: { children: ReactNode }) => {
  const [drivers, setDrivers] = useState<Record<string, any>>({});
  const [vehicles, setVehicles] = useState<Record<string, any>>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const driversRef = ref(database, "drivers");
    const unsubscribe = onValue(driversRef, (snapshot) => {
      setDrivers(snapshot.val() || {});
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const vehiclesRef = ref(database, "vehicles");
    const unsubscribe = onValue(vehiclesRef, (snapshot) => {
      setVehicles(snapshot.val() || {});
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const suggestionsRef = query(ref(database, "suggestions"), limitToFirst(200));
    const unsubscribe = onValue(suggestionsRef, (snapshot) => {
      const value = snapshot.val() || {};
      setSuggestions(Object.values(value));
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const driversList = useMemo(() =>
    Object.entries(drivers || {}).map(([id, data]) => ({ id, data })),
  [drivers]);

  const vehiclesList = useMemo(() =>
    Object.entries(vehicles || {}).map(([id, data]) => ({ id, data })),
  [vehicles]);

  return (
    <CatalogContext.Provider
      value={{
        drivers: driversList,
        vehicles: vehiclesList,
        suggestions,
        driversMap: drivers,
        vehiclesMap: vehicles,
        loading,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const context = useContext(CatalogContext);

  if (!context) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }

  return context;
};
